import * as React from "react";
import { Search } from "lucide-react";

import { ErrorAlert } from "@/components/ui/errorAlert";
import { Loader } from "@/components/ui/loader";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { useGetSnapshotViewsQuery } from "@/modules/snapshots/snapshotViews.api";
import { useGetDirectoryUsersQuery } from "@/api/users-admin";
import { buildSnapshotViewHierarchy } from "./snapshotViewHierarchy";
import { SnapshotViewsHierarchy } from "./SnapshotViewsHierarchy";

export const SnapshotViewsWorkspace: React.FC = () => {
  const [query, setQuery] = React.useState("");
  const [page, setPage] = React.useState(0);
  const pageSize = 25;
  const viewsQuery = useGetSnapshotViewsQuery({
    query,
    limit: pageSize,
    offset: page * pageSize,
    sort_field: "hierarchy",
    sort_direction: "asc",
  });
  const views = React.useMemo(() => viewsQuery.data?.items ?? [], [viewsQuery.data?.items]);
  const ownerIds = React.useMemo(() => [...new Set(views.map(view => view.owner_id))], [views]);
  const ownersQuery = useGetDirectoryUsersQuery(ownerIds, { skip: ownerIds.length === 0 });
  const { formatDateTime } = useDateFormatter();
  const ownersById = React.useMemo(
    () => new Map((ownersQuery.data ?? []).map(owner => [owner.id, owner])),
    [ownersQuery.data],
  );
  const groups = React.useMemo(() => buildSnapshotViewHierarchy(views), [views]);

  if (viewsQuery.isLoading) return <Loader />;
  if (viewsQuery.error) return <ErrorAlert title="Unable to load views" error={viewsQuery.error} />;

  return (
    <section className="min-h-[calc(100vh-4rem)] bg-[#f7f3ee] px-8 py-7">
      <header className="mb-6 flex items-end gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Snapshots / Views</p>
          <h1 className="mt-1 text-3xl font-bold text-[#111b35]">Views</h1>
          <p className="mt-1 text-sm text-gray-600">Live, reusable views over your latest collection data.</p>
        </div>
        <label className="relative ml-auto w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <input
            aria-label="Search views"
            className="h-10 w-full rounded-md border border-[#ddd5cb] bg-white pl-9 pr-3 text-sm outline-none"
            onChange={event => {
              setQuery(event.target.value);
              setPage(0);
            }}
            placeholder="Search views"
            type="search"
            value={query}
          />
        </label>
      </header>

      <div className="overflow-hidden rounded-lg border border-[#ddd5cb] bg-white">
        <SnapshotViewsHierarchy groups={groups} ownersById={ownersById} formatDateTime={formatDateTime} />
        {viewsQuery.data?.items.length === 0 && (
          <div className="px-6 py-14 text-center text-sm text-gray-600">No views match your search.</div>
        )}
        {(viewsQuery.data?.total ?? 0) > pageSize && (
          <div className="flex items-center justify-end gap-3 border-t border-[#ebe5de] px-5 py-3">
            <span className="text-sm text-gray-600">
              {page * pageSize + 1}–{Math.min((page + 1) * pageSize, viewsQuery.data?.total ?? 0)} of{" "}
              {viewsQuery.data?.total}
            </span>
            <button
              className="rounded border px-3 py-1 text-sm disabled:opacity-50"
              disabled={page === 0}
              onClick={() => setPage(value => value - 1)}
            >
              Previous
            </button>
            <button
              className="rounded border px-3 py-1 text-sm disabled:opacity-50"
              disabled={(page + 1) * pageSize >= (viewsQuery.data?.total ?? 0)}
              onClick={() => setPage(value => value + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
